/* AQUON Instagram Feed.

   The cards are already on screen from Liquid before this file runs. All this
   does is (a) drive play/mute for the section's own videos and (b), when a
   token is configured, swap fresh Instagram media into those same cards.

   No layout shift: .aqrp__card carries a fixed aspect-ratio, so replacing the
   media inside it cannot change the card box. If the request fails for any
   reason the server-rendered fallback is simply left alone. */
(function () {
  'use strict';

  var FIELDS = 'id,caption,media_type,media_url,permalink,thumbnail_url,timestamp,username';
  var ENDPOINT = 'https://graph.instagram.com/me/media';

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- video playback ----------
     Scoped to this section. AQUON Real People binds the same behaviour at the
     document level behind window.aqrpInit; when both sections are on a page its
     delegated listeners already cover these cards, so we only wire what is ours
     and never re-bind globally. */
  function bindVideos(root) {
    root.addEventListener('click', function (event) {
      // Unmute toggle, matching Real People: only one card audible at a time.
      var mute = event.target.closest('.aqrp__mute');
      if (mute) {
        var muteCard = mute.closest('.aqrp__card');
        var muteVideo = muteCard && muteCard.querySelector('.aqrp__video');
        if (!muteVideo) return;
        event.preventDefault();
        if (muteVideo.muted) {
          document.querySelectorAll('.aqrp__card.is-unmuted').forEach(function (other) {
            if (other === muteCard) return;
            other.classList.remove('is-unmuted');
            var ov = other.querySelector('.aqrp__video');
            if (ov) ov.muted = true;
            var ob = other.querySelector('.aqrp__mute');
            if (ob) ob.setAttribute('aria-pressed', 'false');
          });
        }
        muteVideo.muted = !muteVideo.muted;
        muteCard.classList.toggle('is-unmuted', !muteVideo.muted);
        mute.setAttribute('aria-pressed', String(!muteVideo.muted));
        var mp = muteVideo.play();
        if (mp && mp.catch) mp.catch(function () {});
        return;
      }

      var play = event.target.closest('.aqrp__play');
      if (play) {
        var card = play.closest('.aqrp__card');
        var video = card && card.querySelector('.aqrp__video');
        if (!video) return;
        event.preventDefault();
        card.classList.add('is-playing');
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
        video.addEventListener('ended', function () {
          card.classList.remove('is-playing');
          video.currentTime = 0;
        }, { once: true });
      }
    });
  }

  /* Autoplay only while mostly on screen. Mobile browsers cap concurrent
     playback, so on the swipe row this keeps the slot for the centred card. */
  function observeAutoplay(root) {
    if (root.dataset.autoplay !== 'true') return null;
    if (reduceMotion() || !('IntersectionObserver' in window)) return null;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.intersectionRatio >= 0.55) {
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: [0, 0.25, 0.55, 0.8, 1] });

    root.querySelectorAll('.aqrp__video').forEach(function (v) { io.observe(v); });
    return io;
  }

  /* ---------- live refresh ---------- */
  function buildImage(item) {
    var img = document.createElement('img');
    img.className = 'aqrp__poster-img';
    img.src = item.media_type === 'VIDEO' ? (item.thumbnail_url || item.media_url) : item.media_url;
    img.alt = item.caption ? String(item.caption).slice(0, 120) : 'Instagram post';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
    return img;
  }

  function buildVideo(item, autoplay) {
    var video = document.createElement('video');
    video.className = 'aqrp__video';
    video.src = item.media_url;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.preload = 'none';
    if (item.thumbnail_url) video.poster = item.thumbnail_url;
    if (autoplay) {
      video.loop = true;
      video.muted = true;
      video.setAttribute('muted', '');
    } else {
      video.controls = true;
    }
    return video;
  }

  function applyItem(card, item, opts) {
    var poster = card.querySelector('[data-aqig-poster]');
    var slot = card.querySelector('[data-aqig-video-slot]');
    var link = card.querySelector('[data-aqig-link]');
    var caption = card.querySelector('[data-aqig-caption]');
    var user = card.querySelector('[data-aqig-user]');

    if (poster) {
      poster.innerHTML = '';
      poster.appendChild(buildImage(item));
    }

    if (slot) {
      slot.innerHTML = '';
      var isVideo = item.media_type === 'VIDEO';
      if (isVideo) slot.appendChild(buildVideo(item, opts.autoplay));
      // Play button only matters when autoplay is off and there is a video.
      var play = card.querySelector('.aqrp__play');
      if (play) play.hidden = !isVideo || opts.autoplay;
    }

    if (link && item.permalink) {
      link.href = item.permalink;
      link.setAttribute(
        'aria-label',
        (item.caption ? String(item.caption).slice(0, 90) : 'Instagram post') + ' — open on Instagram'
      );
    }
    if (caption && item.caption) caption.textContent = item.caption;
    if (user && item.username) user.textContent = '@' + String(item.username).replace(/^@/, '');
  }

  function refresh(root) {
    var token = root.dataset.token;
    if (!token) return;

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-aqig-card]'));
    if (!cards.length) return;

    var count = parseInt(root.dataset.count, 10) || cards.length;
    var url = ENDPOINT + '?fields=' + encodeURIComponent(FIELDS) +
              '&limit=' + encodeURIComponent(count) +
              '&access_token=' + encodeURIComponent(token);

    var controller = ('AbortController' in window) ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, 8000);

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (res) {
        if (!res.ok) throw new Error('Instagram responded ' + res.status);
        return res.json();
      })
      .then(function (payload) {
        clearTimeout(timer);
        var items = (payload && payload.data) || [];
        if (!items.length) return; // keep the fallback

        root.classList.add('is-refreshing');
        var opts = { autoplay: root.dataset.autoplay === 'true' };

        // Only fill cards we actually have; never add or remove cards, so the
        // row length (and therefore the layout) is fixed by the merchant.
        cards.forEach(function (card, i) {
          if (items[i]) applyItem(card, items[i], opts);
        });

        requestAnimationFrame(function () {
          root.classList.remove('is-refreshing');
          observeAutoplay(root);
        });
      })
      .catch(function (err) {
        clearTimeout(timer);
        // Deliberately quiet in production: the fallback is already correct, so
        // a failed refresh is not a user-facing error. Surfaced in the editor
        // so the merchant can see why nothing changed.
        if (window.Shopify && window.Shopify.designMode) {
          console.warn('[AQUON Instagram] live refresh skipped:', err && err.message);
        }
      });
  }

  function init(root) {
    if (root.dataset.aqigReady === '1') return;
    root.dataset.aqigReady = '1';

    bindVideos(root);
    observeAutoplay(root);

    if (root.dataset.live === 'true') refresh(root);
  }

  function boot() {
    document.querySelectorAll('[data-aqig]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme Editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector ? event.target.querySelector('[data-aqig]') : null;
    if (root) init(root);
  });
})();
